import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { apiGet, apiPost, apiPut, apiDelete, getErrorMessage } from '../../utils/api'

/* ─── Types ────────────────────────────────────────────────────────────── */

interface Contact {
  id?: string
  first_name: string
  last_name?: string
  email?: string
  phone?: string
  company?: string
  labels?: string[]
  notes?: string
  linked_groups_count?: number
  updated_at?: string
  contributed_by?: string[]
  source_count?: number
  source?: string
}

interface ContactSource {
  id: string
  name: string
  type: string
  agent_id: string
  agent_name?: string
  last_synced_at?: string
  contact_count: number
  created_at: string
}

interface SourceContact {
  id: string
  source_id: string
  master_contact_id?: string
  first_name?: string
  last_name?: string
  phone?: string
  phone_normalised?: string
  email?: string
  company?: string
  merge_status: string
  merge_confidence: number
  source_name?: string
  source_type?: string
  master_first_name?: string
  master_last_name?: string
  master_phone?: string
  master_email?: string
  master_company?: string
  created_at: string
}

interface MergeLogEntry {
  id: string
  source_contact_id: string
  master_contact_id: string
  match_type: string
  confidence: number
  action: string
  approved_by?: string
  approved_by_name?: string
  fields_updated: string[]
  sc_first_name?: string
  sc_last_name?: string
  sc_phone?: string
  source_name?: string
  created_at: string
}

interface ContactStats {
  total_contacts: number
  total_sources: number
  total_source_contacts: number
  pending_merges: number
  auto_merged: number
  manual_merged: number
  source_breakdown: ContactSource[]
  recent_merges: any[]
}

interface ImportPreviewRow {
  [key: string]: string
}

type SortField = 'name' | 'email' | 'phone' | 'company' | 'labels' | 'linked_groups_count' | 'updated_at' | 'source_count'
type SortDirection = 'asc' | 'desc'
type ActiveView = 'master' | 'my-contacts' | 'by-source' | 'merge-queue' | 'import-history'

/* ─── Source type config ─────────────────────────────────────────────── */

const SOURCE_TYPE_CONFIG: Record<string, { label: string; icon: string; bgColor: string; textColor: string }> = {
  iphone: { label: 'iPhone', icon: '📱', bgColor: 'bg-blue-100', textColor: 'text-blue-700' },
  whatsapp: { label: 'WhatsApp', icon: '💬', bgColor: 'bg-green-100', textColor: 'text-green-700' },
  android: { label: 'Android', icon: '🤖', bgColor: 'bg-teal-100', textColor: 'text-teal-700' },
  manual: { label: 'Manual', icon: '✏️', bgColor: 'bg-slate-100', textColor: 'text-slate-700' },
  csv: { label: 'CSV', icon: '📄', bgColor: 'bg-amber-100', textColor: 'text-amber-700' },
  api: { label: 'API', icon: '⚡', bgColor: 'bg-purple-100', textColor: 'text-purple-700' },
}

/* ─── Avatar color palette ──────────────────────────────────────────── */

const AVATAR_COLORS = [
  '#25D366', '#34B7F1', '#E91E63', '#9C27B0', '#FF9800',
  '#00BCD4', '#FF5722', '#3F51B5', '#8BC34A', '#795548',
  '#2196F3', '#4CAF50', '#FF6F00', '#00796B', '#512DA8',
]

function getColorForContact(name: string): string {
  let hash = 0
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash)
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length]
}

function getInitials(firstName: string, lastName?: string): string {
  const f = (firstName || '').trim()[0]?.toUpperCase()
  const l = (lastName || '').trim()[0]?.toUpperCase()
  return `${f || ''}${l || ''}`.slice(0, 2) || '?'
}

const ContactAvatar: React.FC<{ firstName?: string; lastName?: string; size?: 'sm' | 'md' | 'lg' }> = ({
  firstName = '',
  lastName = '',
  size = 'md',
}) => {
  const sizeClasses = { sm: 'w-8 h-8 text-xs', md: 'w-10 h-10 text-sm', lg: 'w-12 h-12 text-base' }
  const initials = getInitials(firstName, lastName)
  const color = getColorForContact(`${firstName} ${lastName}`)

  return (
    <div
      className={`${sizeClasses[size]} rounded-full flex items-center justify-center flex-shrink-0 font-semibold text-white`}
      style={{ backgroundColor: color }}
    >
      {initials}
    </div>
  )
}

const SourceBadge: React.FC<{ type: string; label?: string; size?: 'sm' | 'md' }> = ({ type, label, size = 'sm' }) => {
  const config = SOURCE_TYPE_CONFIG[type] || SOURCE_TYPE_CONFIG.manual
  const sizeClass = size === 'sm' ? 'text-xs px-2 py-0.5' : 'text-sm px-3 py-1'
  return (
    <span className={`inline-flex items-center gap-1 rounded-full ${sizeClass} ${config.bgColor} ${config.textColor} font-medium`}>
      <span>{config.icon}</span>
      <span>{label || config.label}</span>
    </span>
  )
}

/* ─── vCard Parser ──────────────────────────────────────────────────── */

function parseVCard(text: string): Contact[] {
  const contacts: Contact[] = []
  const cards = text.split('BEGIN:VCARD')

  for (const card of cards) {
    if (!card.trim()) continue
    const lines = card.split(/\r?\n/)
    const contact: any = {}

    for (const line of lines) {
      if (line.startsWith('FN:') || line.startsWith('FN;')) {
        const fullName = line.split(':').slice(1).join(':').trim()
        const parts = fullName.split(' ')
        contact.first_name = parts[0] || ''
        contact.last_name = parts.slice(1).join(' ') || ''
      }
      if (line.startsWith('N:') || line.startsWith('N;')) {
        const parts = line.split(':').slice(1).join(':').split(';')
        contact.last_name = contact.last_name || (parts[0]?.trim() || '')
        contact.first_name = contact.first_name || (parts[1]?.trim() || '')
      }
      if ((line.startsWith('TEL') || line.startsWith('tel')) && !contact.phone) {
        contact.phone = line.split(':').slice(1).join(':').replace(/[\s\-()]/g, '').trim()
      }
      if ((line.startsWith('EMAIL') || line.startsWith('email')) && !contact.email) {
        contact.email = line.split(':').slice(1).join(':').trim()
      }
      if ((line.startsWith('ORG') || line.startsWith('org')) && !contact.company) {
        contact.company = line.split(':').slice(1).join(':').split(';')[0]?.trim()
      }
    }

    if (contact.first_name || contact.last_name || contact.phone) {
      contacts.push(contact)
    }
  }

  return contacts
}

/* ─── CSV Parser ────────────────────────────────────────────────────── */

function parseCSV(text: string): { headers: string[]; rows: string[][] } {
  const lines = text.split(/\r?\n/).filter((l) => l.trim())
  const headers = lines[0].split(',').map((h) => h.replace(/^"|"$/g, '').trim())
  const rows = lines.slice(1).map((line) => {
    const result: string[] = []
    let current = ''
    let inQuotes = false
    for (const char of line) {
      if (char === '"') {
        inQuotes = !inQuotes
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim())
        current = ''
      } else {
        current += char
      }
    }
    result.push(current.trim())
    return result
  })
  return { headers, rows }
}

/* ─── Excel Parser (basic) ──────────────────────────────────────────── */

async function parseExcel(file: File): Promise<{ headers: string[]; rows: string[][] }> {
  return { headers: [], rows: [] }
}

/* ─── Debounce hook ─────────────────────────────────────────────────── */

function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value)

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => clearTimeout(handler)
  }, [value, delay])

  return debouncedValue
}

/* ─── Main Component ───────────────────────────────────────────────────── */

export const ContactsView: React.FC = () => {
  // State - navigation
  const [activeView, setActiveView] = useState<ActiveView>('master')

  // State - contacts
  const [contacts, setContacts] = useState<Contact[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // State - search & filter. Seed the search from ?search= so "Manage in
  // Contacts" from a company detail lands pre-filtered to that company.
  const [sp] = useSearchParams()
  const [searchQuery, setSearchQuery] = useState(sp.get('search') || '')
  const debouncedSearch = useDebounce(searchQuery, 300)
  const [labelFilters, setLabelFilters] = useState<string[]>([])
  const [allLabels, setAllLabels] = useState<string[]>([])
  const [sourceFilters, setSourceFilters] = useState<string[]>([])
  const [allSources, setAllSources] = useState<ContactSource[]>([])

  // State - sorting
  const [sortField, setSortField] = useState<SortField>('name')
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc')

  // State - pagination
  const [pageSize, setPageSize] = useState(25)
  const [currentPage, setCurrentPage] = useState(1)

  // State - selection
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())

  // State - detail panel
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null)
  const [editingContact, setEditingContact] = useState<Contact | null>(null)
  const [showDetailPanel, setShowDetailPanel] = useState(false)
  const [detailPanelTab, setDetailPanelTab] = useState<'details' | 'sources'>('details')
  const [contactSources, setContactSources] = useState<SourceContact[]>([])
  const [contactMergeHistory, setContactMergeHistory] = useState<MergeLogEntry[]>([])

  // State - modals
  const [showNewContactModal, setShowNewContactModal] = useState(false)
  const [showImportModal, setShowImportModal] = useState(false)
  const [importStep, setImportStep] = useState<'source' | 'file' | 'map' | 'result'>(
    'source'
  )
  const [importFile, setImportFile] = useState<File | null>(null)
  const [importPreview, setImportPreview] = useState<{ headers: string[]; rows: ImportPreviewRow[] } | null>(null)
  const [importFieldMap, setImportFieldMap] = useState<Record<string, string>>({})
  const [importProgress, setImportProgress] = useState<{
    importing: boolean
    count: number
    total: number
    created: number
    merged: number
    queued: number
    skipped: number
  }>({ importing: false, count: 0, total: 0, created: 0, merged: 0, queued: 0, skipped: 0 })
  const [selectedSource, setSelectedSource] = useState<ContactSource | null>(null)
  const [newSourceName, setNewSourceName] = useState('')
  const [newSourceType, setNewSourceType] = useState<string>('csv')

  // State - by-source view
  const [selectedSourceForView, setSelectedSourceForView] = useState<ContactSource | null>(null)
  const [sourceContacts, setSourceContacts] = useState<SourceContact[]>([])
  const [sourceContactsLoading, setSourceContactsLoading] = useState(false)

  // State - merge queue
  const [mergeQueue, setMergeQueue] = useState<SourceContact[]>([])
  const [mergeQueueLoading, setMergeQueueLoading] = useState(false)

  // State - form
  const [formData, setFormData] = useState<Contact>({ first_name: '', last_name: '', labels: [] })
  const [newLabelInput, setNewLabelInput] = useState('')

  const fileInputRef = useRef<HTMLInputElement>(null)
  const LEE_AGENT_ID = '1'

  /* ── Fetch contacts ─────────────────────────────────────────────── */
  const fetchContacts = useCallback(async (myContactsOnly: boolean = false) => {
    try {
      setLoading(true)
      const params: any = { pageSize: 5000 }
      if (debouncedSearch) params.search = debouncedSearch
      if (labelFilters.length > 0) params.label = labelFilters
      if (sourceFilters.length > 0) params.source_id = sourceFilters
      if (myContactsOnly) params.contributor = LEE_AGENT_ID

      const res = await apiGet<any>('/contacts', params)
      const list = Array.isArray(res) ? res : res.contacts || res.data || []
      setContacts(list)

      // Extract all unique labels
      const labels = new Set<string>()
      list.forEach((c: any) => {
        c.labels?.forEach((l: string) => labels.add(l))
      })
      setAllLabels(Array.from(labels).sort())

      setError(null)
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load contacts'))
    } finally {
      setLoading(false)
    }
  }, [debouncedSearch, labelFilters, sourceFilters])

  const fetchSources = useCallback(async () => {
    try {
      const res = await apiGet<{ sources: ContactSource[] }>('/contact-sources')
      setAllSources(res.sources || [])
    } catch (err) {
      console.error('Failed to load sources:', err)
    }
  }, [])

  const fetchMergeQueue = useCallback(async () => {
    try {
      setMergeQueueLoading(true)
      const res = await apiGet<{ queue: SourceContact[] }>('/contacts/merge-queue')
      setMergeQueue(res.queue || [])
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load merge queue'))
    } finally {
      setMergeQueueLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchContacts(activeView === 'my-contacts')
  }, [fetchContacts, activeView])

  useEffect(() => {
    fetchSources()
  }, [fetchSources])

  useEffect(() => {
    if (activeView === 'merge-queue') {
      fetchMergeQueue()
    }
  }, [activeView, fetchMergeQueue])

  /* ── Fetch contact sources and history ──────────────────────────── */
  const fetchContactSources = useCallback(async (contactId: string) => {
    try {
      const [sourcesRes, historyRes] = await Promise.all([
        apiGet<{ sources: SourceContact[] }>(`/contacts/${contactId}/sources`),
        apiGet<{ history: MergeLogEntry[] }>(`/contacts/${contactId}/merge-history`),
      ])
      setContactSources(sourcesRes.sources || [])
      setContactMergeHistory(historyRes.history || [])
    } catch (err) {
      console.error('Failed to fetch contact sources/history:', err)
    }
  }, [])

  /* ── Filter & sort contacts ─────────────────────────────────────── */
  const filteredContacts = useMemo(() => {
    let filtered = [...contacts]

    // Text search
    if (debouncedSearch) {
      const q = debouncedSearch.toLowerCase()
      filtered = filtered.filter((c) => {
        const name = `${c.first_name} ${c.last_name}`.toLowerCase()
        const email = (c.email || '').toLowerCase()
        const phone = (c.phone || '').toLowerCase()
        const company = (c.company || '').toLowerCase()
        return name.includes(q) || email.includes(q) || phone.includes(q) || company.includes(q)
      })
    }

    // Label filter
    if (labelFilters.length > 0) {
      filtered = filtered.filter((c) => labelFilters.some((l) => c.labels?.includes(l)))
    }

    // Source filter
    if (sourceFilters.length > 0) {
      filtered = filtered.filter((c) => sourceFilters.some((s) => c.source_count && c.source_count > 0))
    }

    // Sort
    filtered.sort((a, b) => {
      let aVal: any = ''
      let bVal: any = ''

      if (sortField === 'name') {
        aVal = `${a.first_name} ${a.last_name}`.toLowerCase()
        bVal = `${b.first_name} ${b.last_name}`.toLowerCase()
      } else if (sortField === 'email') {
        aVal = (a.email || '').toLowerCase()
        bVal = (b.email || '').toLowerCase()
      } else if (sortField === 'phone') {
        aVal = (a.phone || '').toLowerCase()
        bVal = (b.phone || '').toLowerCase()
      } else if (sortField === 'company') {
        aVal = (a.company || '').toLowerCase()
        bVal = (b.company || '').toLowerCase()
      } else if (sortField === 'labels') {
        aVal = (a.labels || []).join(',').toLowerCase()
        bVal = (b.labels || []).join(',').toLowerCase()
      } else if (sortField === 'linked_groups_count') {
        aVal = a.linked_groups_count || 0
        bVal = b.linked_groups_count || 0
      } else if (sortField === 'source_count') {
        aVal = a.source_count || 0
        bVal = b.source_count || 0
      } else if (sortField === 'updated_at') {
        aVal = new Date(a.updated_at || '').getTime()
        bVal = new Date(b.updated_at || '').getTime()
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1
      return 0
    })

    return filtered
  }, [contacts, debouncedSearch, labelFilters, sourceFilters, sortField, sortDirection])

  // Paginate
  const totalPages = Math.ceil(filteredContacts.length / pageSize)
  const paginatedContacts = filteredContacts.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  )

  /* ── Handlers ──────────────────────────────────────────────────── */

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  const SortIcon: React.FC<{ field: SortField }> = ({ field }) => {
    if (sortField !== field) return <span className="text-txt-tertiary">⇅</span>
    return <span>{sortDirection === 'asc' ? '▲' : '▼'}</span>
  }

  const handleSelectRow = (id: string) => {
    const newSelected = new Set(selectedIds)
    if (newSelected.has(id)) newSelected.delete(id)
    else newSelected.add(id)
    setSelectedIds(newSelected)
  }

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(new Set(paginatedContacts.map((c) => c.id || '')))
    } else {
      setSelectedIds(new Set())
    }
  }

  const handleNewContact = async () => {
    try {
      if (!formData.first_name?.trim()) {
        setError('First name is required')
        return
      }

      await apiPost('/contacts', formData)
      setShowNewContactModal(false)
      setFormData({ first_name: '', last_name: '', labels: [] })
      setNewLabelInput('')
      fetchContacts(activeView === 'my-contacts')
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to create contact'))
    }
  }

  const handleSelectContact = (contact: Contact) => {
    setSelectedContact(contact)
    setEditingContact({ ...contact })
    setShowDetailPanel(true)
    setDetailPanelTab('details')
    if (contact.id) {
      fetchContactSources(contact.id)
    }
  }

  const handleSaveContact = async () => {
    try {
      if (!editingContact?.id) return
      await apiPut(`/contacts/${editingContact.id}`, editingContact)
      setShowDetailPanel(false)
      fetchContacts(activeView === 'my-contacts')
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to save contact'))
    }
  }

  const handleDeleteContact = async (id: string) => {
    if (!confirm('Delete this contact?')) return
    try {
      await apiDelete(`/contacts/${id}`)
      fetchContacts(activeView === 'my-contacts')
      setShowDetailPanel(false)
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to delete contact'))
    }
  }

  const handleImportFile = async (file: File) => {
    setImportFile(file)
    try {
      const text = await file.text()

      let parsed: any
      if (file.name.endsWith('.vcf')) {
        const contacts = parseVCard(text)
        const headers = ['first_name', 'last_name', 'email', 'phone', 'company']
        const rows = contacts.map((c) => ({
          first_name: c.first_name || '',
          last_name: c.last_name || '',
          email: c.email || '',
          phone: c.phone || '',
          company: c.company || '',
        }))
        parsed = { headers, rows }
      } else if (file.name.endsWith('.csv')) {
        parsed = parseCSV(text)
      } else if (file.name.endsWith('.xlsx')) {
        parsed = await parseExcel(file)
      } else {
        setError('Unsupported file format. Use .vcf, .csv, or .xlsx')
        return
      }

      setImportPreview(parsed)
      setImportFieldMap({})
      setImportStep('map')
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to parse file'))
    }
  }

  const handleImport = async () => {
    if (!importPreview || !importFile) return

    try {
      setImportProgress({
        importing: true,
        count: 0,
        total: importPreview.rows.length,
        created: 0,
        merged: 0,
        queued: 0,
        skipped: 0,
      })

      let skipped = 0
      const toCreate = []

      for (const row of importPreview.rows) {
        const contact: Partial<Contact> = {}

        for (const [srcCol, destField] of Object.entries(importFieldMap)) {
          const value = row[srcCol] || ''
          if (destField !== 'skip' && value) {
            ;(contact as any)[destField] = value
          }
        }

        if (!contact.first_name && !contact.email && !contact.phone) {
          skipped++
          continue
        }

        toCreate.push(contact)
      }

      // Prepare source data
      const sourceData = selectedSource
        ? { source_id: selectedSource.id }
        : { source_name: newSourceName, source_type: newSourceType }

      // Call import endpoint
      const result = await apiPost('/contacts/import-with-source', {
        ...sourceData,
        contacts: toCreate,
      })

      setImportProgress({
        importing: false,
        count: importPreview.rows.length,
        total: importPreview.rows.length,
        created: result.imported || 0,
        merged: result.merged || 0,
        queued: result.queued || 0,
        skipped: skipped,
      })

      setImportStep('result')
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to import contacts'))
    }
  }

  const handleExport = async () => {
    try {
      const response = await fetch('/api/contacts/export/csv-download', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('auth_token') || ''}`,
        },
      })
      const blob = await response.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `contacts_${new Date().toISOString().split('T')[0]}.csv`
      a.click()
      URL.revokeObjectURL(url)
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to export contacts'))
    }
  }

  const handleBulkDelete = async () => {
    if (!confirm(`Delete ${selectedIds.size} contacts?`)) return
    try {
      await Promise.all(Array.from(selectedIds).map((id) => apiDelete(`/contacts/${id}`)))
      setSelectedIds(new Set())
      fetchContacts(activeView === 'my-contacts')
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to delete contacts'))
    }
  }

  const handleBulkAddLabel = async (label: string) => {
    try {
      await Promise.all(
        Array.from(selectedIds).map((id) => {
          const contact = contacts.find((c) => c.id === id)
          if (!contact) return
          const labels = new Set(contact.labels || [])
          labels.add(label)
          return apiPut(`/contacts/${id}`, { ...contact, labels: Array.from(labels) })
        })
      )
      setSelectedIds(new Set())
      fetchContacts(activeView === 'my-contacts')
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to add labels'))
    }
  }

  const handleFetchSourceContacts = useCallback(async (source: ContactSource) => {
    try {
      setSourceContactsLoading(true)
      const res = await apiGet<{ contacts: SourceContact[] }>(`/contact-sources/${source.id}/contacts`)
      setSourceContacts(res.contacts || [])
      setSelectedSourceForView(source)
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load source contacts'))
    } finally {
      setSourceContactsLoading(false)
    }
  }, [])

  const handleApproveMerge = async (mergeId: string) => {
    try {
      await apiPost(`/contacts/merge-queue/${mergeId}/approve`, {})
      fetchMergeQueue()
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to approve merge'))
    }
  }

  const handleRejectMerge = async (mergeId: string) => {
    try {
      await apiPost(`/contacts/merge-queue/${mergeId}/reject`, {})
      fetchMergeQueue()
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to reject merge'))
    }
  }

  /* ── Render master/my-contacts directory ─────────────────────────── */

  const renderContactsDirectory = () => (
    <div className="h-full flex flex-col bg-surface">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-border px-6 py-4">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold text-txt-primary">
            {activeView === 'my-contacts' ? 'My Contacts' : 'Master Directory'}
          </h1>
          <div className="flex items-center gap-2">
            <button
              onClick={handleExport}
              className="px-4 py-2 rounded-lg bg-slate-200 text-slate-700 hover:bg-slate-300 text-sm font-medium transition-colors"
            >
              Export
            </button>
            <button
              onClick={() => {
                setShowImportModal(true)
                setImportStep('source')
                setSelectedSource(null)
                setNewSourceName('')
                setNewSourceType('csv')
                setImportFile(null)
                setImportPreview(null)
              }}
              className="px-4 py-2 rounded-lg bg-slate-200 text-slate-700 hover:bg-slate-300 text-sm font-medium transition-colors"
            >
              Import
            </button>
            <button
              onClick={() => {
                setShowNewContactModal(true)
                setFormData({ first_name: '', last_name: '', labels: [] })
              }}
              className="px-4 py-2 rounded-lg bg-blue-500 text-white hover:bg-blue-600 text-sm font-medium transition-colors"
            >
              New Contact
            </button>
          </div>
        </div>

        {error && (
          <div className="mt-3 px-4 py-2 rounded bg-red-100 text-red-700 text-sm">{error}</div>
        )}

        {/* Search bar and filters */}
        <div className="mt-4 space-y-3">
          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-txt-tertiary"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <input
                type="text"
                placeholder="Search by name, email, phone, company..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value)
                  setCurrentPage(1)
                }}
                className="w-full pl-10 pr-4 py-2.5 rounded-lg bg-surface-secondary border border-border text-sm text-txt-primary placeholder-txt-tertiary outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500/20"
              />
            </div>

            {/* Source filter dropdown */}
            {allSources.length > 0 && (
              <select
                multiple
                value={sourceFilters}
                onChange={(e) => {
                  const selected = Array.from(e.target.selectedOptions).map((o) => o.value)
                  setSourceFilters(selected)
                  setCurrentPage(1)
                }}
                className="px-3 py-2.5 rounded-lg border border-border text-sm bg-surface-secondary text-txt-primary"
              >
                <option value="" disabled>
                  Filter by source
                </option>
                {allSources.map((source) => (
                  <option key={source.id} value={source.id}>
                    {source.name} ({source.contact_count})
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Label filter pills */}
          {allLabels.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              {allLabels.map((label) => (
                <button
                  key={label}
                  onClick={() => {
                    const updated = new Set(labelFilters)
                    if (updated.has(label)) {
                      updated.delete(label)
                    } else {
                      updated.add(label)
                    }
                    setLabelFilters(Array.from(updated))
                    setCurrentPage(1)
                  }}
                  className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-all cursor-pointer ${
                    labelFilters.includes(label)
                      ? 'bg-blue-100 text-blue-700 border border-blue-300'
                      : 'bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-200'
                  }`}
                >
                  {label}
                </button>
              ))}
              {labelFilters.length > 0 && (
                <button
                  onClick={() => {
                    setLabelFilters([])
                    setCurrentPage(1)
                  }}
                  className="text-xs text-txt-tertiary hover:text-txt-secondary ml-2"
                >
                  Clear
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col min-h-0">
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="flex flex-col items-center gap-3">
              <svg
                className="w-8 h-8 text-brand-500 animate-spin"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              <span className="text-sm text-txt-secondary">Loading contacts...</span>
            </div>
          </div>
        ) : filteredContacts.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48 text-center px-6">
            <svg
              className="w-12 h-12 text-txt-tertiary mb-3"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
              />
            </svg>
            <p className="text-sm font-medium text-txt-secondary">No contacts found</p>
            <p className="text-xs text-txt-tertiary mt-1">Create or import contacts to get started</p>
          </div>
        ) : (
          <>
            {/* Fixed table header */}
            <div className="flex-shrink-0 bg-slate-50 border-b border-border">
              <table className="w-full table-fixed">
                <colgroup>
                  <col style={{ width: '40px' }} />
                  <col style={{ width: '20%' }} />
                  <col style={{ width: '18%' }} />
                  <col style={{ width: '14%' }} />
                  <col style={{ width: '14%' }} />
                  <col style={{ width: '12%' }} />
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '12%' }} />
                </colgroup>
                <thead>
                  <tr>
                    <th className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={
                          selectedIds.size === paginatedContacts.length &&
                          paginatedContacts.length > 0
                        }
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        className="rounded"
                      />
                    </th>
                    {[
                      { field: 'name' as SortField, label: 'Name' },
                      { field: 'email' as SortField, label: 'Email' },
                      { field: 'phone' as SortField, label: 'Phone' },
                      { field: 'company' as SortField, label: 'Company' },
                      { field: 'labels' as SortField, label: 'Labels' },
                      { field: 'source_count' as SortField, label: 'Sources' },
                      { field: 'updated_at' as SortField, label: 'Last Updated' },
                    ].map(({ field, label }) => (
                      <th
                        key={field}
                        onClick={() => handleSort(field)}
                        className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider cursor-pointer hover:text-slate-700 select-none transition-colors"
                      >
                        <div className="flex items-center gap-1.5">
                          {label}
                          <SortIcon field={field} />
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
              </table>
            </div>

            {/* Scrollable table body */}
            <div className="flex-1 overflow-y-auto overflow-x-hidden">
              <table className="w-full table-fixed">
                <colgroup>
                  <col style={{ width: '40px' }} />
                  <col style={{ width: '20%' }} />
                  <col style={{ width: '18%' }} />
                  <col style={{ width: '14%' }} />
                  <col style={{ width: '14%' }} />
                  <col style={{ width: '12%' }} />
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '12%' }} />
                </colgroup>
                <tbody>
                  {paginatedContacts.map((contact) => (
                    <tr
                      key={contact.id}
                      onClick={() => handleSelectContact(contact)}
                      className="border-b border-border/60 hover:bg-blue-50/40 cursor-pointer transition-colors"
                    >
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(contact.id || '')}
                          onChange={() => handleSelectRow(contact.id || '')}
                          className="rounded"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <ContactAvatar
                            firstName={contact.first_name}
                            lastName={contact.last_name}
                          />
                          <span className="text-sm font-medium text-txt-primary truncate">
                            {contact.first_name} {contact.last_name}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-txt-secondary truncate">
                        {contact.email || '—'}
                      </td>
                      <td className="px-4 py-3 text-sm text-txt-secondary truncate">
                        {contact.phone || '—'}
                      </td>
                      <td className="px-4 py-3 text-sm text-txt-secondary truncate">
                        {contact.company || '—'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {contact.labels?.slice(0, 2).map((label) => (
                            <span
                              key={label}
                              className="inline-block px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-700"
                            >
                              {label}
                            </span>
                          ))}
                          {(contact.labels?.length || 0) > 2 && (
                            <span className="text-xs text-txt-tertiary">
                              +{contact.labels!.length - 2}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {contact.source_count && contact.source_count > 0 ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
                            {contact.source_count}
                          </span>
                        ) : (
                          <span className="text-sm text-txt-tertiary">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-txt-secondary truncate">
                        {contact.updated_at
                          ? new Date(contact.updated_at).toLocaleDateString()
                          : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Pagination */}
      {filteredContacts.length > 0 && (
        <div className="flex-shrink-0 border-t border-border px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <label className="text-sm text-txt-secondary">Rows per page:</label>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(parseInt(e.target.value))
                  setCurrentPage(1)
                }}
                className="px-2 py-1 rounded border border-border text-sm"
              >
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </select>
            </div>
            <span className="text-sm text-txt-secondary">
              {Math.min((currentPage - 1) * pageSize + 1, filteredContacts.length)} -{' '}
              {Math.min(currentPage * pageSize, filteredContacts.length)} of {filteredContacts.length}
            </span>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 rounded border border-border text-sm hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="text-sm text-txt-secondary">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1 rounded border border-border text-sm hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Bulk Actions Bar */}
      {selectedIds.size > 0 && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 bg-white rounded-lg shadow-lg border border-border px-6 py-4 flex items-center gap-4">
          <span className="text-sm font-medium text-txt-primary">{selectedIds.size} selected</span>
          <button
            onClick={() => setSelectedIds(new Set())}
            className="px-3 py-1 text-sm text-txt-secondary hover:text-txt-primary"
          >
            Deselect All
          </button>
          <div className="border-l border-border h-4" />
          <button
            onClick={() => {
              const label = prompt('Enter label name:')
              if (label) handleBulkAddLabel(label)
            }}
            className="px-3 py-1 text-sm text-slate-600 hover:bg-slate-100 rounded transition-colors"
          >
            Add Label
          </button>
          <button
            onClick={handleBulkDelete}
            className="px-3 py-1 text-sm text-red-600 hover:bg-red-50 rounded transition-colors"
          >
            Delete
          </button>
        </div>
      )}
    </div>
  )

  /* ── Render By Source view ──────────────────────────────────────────── */

  const renderBySourceView = () => (
    <div className="h-full flex flex-col bg-surface">
      <div className="flex-shrink-0 border-b border-border px-6 py-4">
        <h1 className="text-xl font-bold text-txt-primary">Contact Sources</h1>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-6">
        {selectedSourceForView ? (
          <>
            <div className="flex items-center gap-4 mb-6">
              <button
                onClick={() => {
                  setSelectedSourceForView(null)
                  setSourceContacts([])
                }}
                className="px-3 py-1 text-sm border border-border rounded hover:bg-slate-50"
              >
                Back to Sources
              </button>
              <h2 className="text-lg font-semibold text-txt-primary">
                {selectedSourceForView.name}
              </h2>
              <SourceBadge type={selectedSourceForView.type} size="md" />
            </div>

            {sourceContactsLoading ? (
              <div className="flex items-center justify-center h-32">
                <span className="text-txt-secondary">Loading contacts...</span>
              </div>
            ) : (
              <div className="space-y-3">
                {sourceContacts.map((contact) => (
                  <div
                    key={contact.id}
                    className="p-4 border border-border rounded-lg hover:bg-slate-50 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="font-medium text-txt-primary">
                          {contact.first_name} {contact.last_name}
                        </p>
                        {contact.email && (
                          <p className="text-sm text-txt-secondary">{contact.email}</p>
                        )}
                        {contact.phone && (
                          <p className="text-sm text-txt-secondary">{contact.phone}</p>
                        )}
                        {contact.company && (
                          <p className="text-sm text-txt-secondary">{contact.company}</p>
                        )}
                      </div>
                      {contact.merge_status && (
                        <span className="text-xs px-2 py-1 rounded-full bg-amber-100 text-amber-700">
                          {contact.merge_status}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {allSources.map((source) => (
              <button
                key={source.id}
                onClick={() => handleFetchSourceContacts(source)}
                className="p-4 border border-border rounded-lg hover:shadow-md hover:border-blue-300 transition-all text-left"
              >
                <div className="flex items-start justify-between mb-3">
                  <h3 className="font-semibold text-txt-primary">{source.name}</h3>
                  <SourceBadge type={source.type} />
                </div>
                <div className="space-y-1 text-sm text-txt-secondary">
                  <p>Agent: {source.agent_name || 'Unknown'}</p>
                  <p>Contacts: {source.contact_count}</p>
                  {source.last_synced_at && (
                    <p>
                      Last synced: {new Date(source.last_synced_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )

  /* ── Render Merge Queue view ────────────────────────────────────────── */

  const renderMergeQueueView = () => (
    <div className="h-full flex flex-col bg-surface">
      <div className="flex-shrink-0 border-b border-border px-6 py-4">
        <h1 className="text-xl font-bold text-txt-primary">
          Merge Queue ({mergeQueue.length} pending)
        </h1>
        <p className="text-sm text-txt-secondary mt-1">
          Review and approve contact merges
        </p>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-6">
        {mergeQueueLoading ? (
          <div className="flex items-center justify-center h-32">
            <span className="text-txt-secondary">Loading merge queue...</span>
          </div>
        ) : mergeQueue.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48">
            <p className="text-txt-secondary">No pending merges</p>
          </div>
        ) : (
          <div className="space-y-6">
            {mergeQueue.map((item) => {
              const confidenceColor =
                item.merge_confidence > 80
                  ? 'bg-green-100 text-green-700'
                  : item.merge_confidence > 60
                    ? 'bg-amber-100 text-amber-700'
                    : 'bg-red-100 text-red-700'

              return (
                <div
                  key={item.id}
                  className="border border-border rounded-lg overflow-hidden"
                >
                  <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50">
                    {/* Source contact */}
                    <div>
                      <p className="text-xs font-semibold text-txt-secondary uppercase mb-2">
                        From: {item.source_name}
                      </p>
                      <div className="space-y-1">
                        <p className="font-medium text-txt-primary">
                          {item.first_name} {item.last_name}
                        </p>
                        {item.email && (
                          <p className="text-sm text-txt-secondary">{item.email}</p>
                        )}
                        {item.phone && (
                          <p className="text-sm text-txt-secondary">{item.phone}</p>
                        )}
                        {item.company && (
                          <p className="text-sm text-txt-secondary">{item.company}</p>
                        )}
                      </div>
                    </div>

                    {/* Master contact */}
                    <div>
                      <p className="text-xs font-semibold text-txt-secondary uppercase mb-2">
                        To: Master
                      </p>
                      <div className="space-y-1">
                        <p className="font-medium text-txt-primary">
                          {item.master_first_name} {item.master_last_name}
                        </p>
                        {item.master_email && (
                          <p className="text-sm text-txt-secondary">{item.master_email}</p>
                        )}
                        {item.master_phone && (
                          <p className="text-sm text-txt-secondary">{item.master_phone}</p>
                        )}
                        {item.master_company && (
                          <p className="text-sm text-txt-secondary">{item.master_company}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="px-4 py-3 bg-white border-t border-border flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <SourceBadge type={item.source_type || 'manual'} size="sm" />
                      <span
                        className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${confidenceColor}`}
                      >
                        {(item.merge_confidence * 100).toFixed(0)}% match
                      </span>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleRejectMerge(item.id)}
                        className="px-4 py-2 text-sm rounded border border-red-300 text-red-600 hover:bg-red-50 transition-colors font-medium"
                      >
                        Keep Separate
                      </button>
                      <button
                        onClick={() => handleApproveMerge(item.id)}
                        className="px-4 py-2 text-sm rounded bg-green-500 text-white hover:bg-green-600 transition-colors font-medium"
                      >
                        Merge
                      </button>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )

  /* ── Render Import History view ──────────────────────────────────────── */

  const renderImportHistoryView = () => (
    <div className="h-full flex flex-col bg-surface">
      <div className="flex-shrink-0 border-b border-border px-6 py-4">
        <h1 className="text-xl font-bold text-txt-primary">Import History</h1>
        <p className="text-sm text-txt-secondary mt-1">All contact sources and imports</p>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-6">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b-2 border-border">
                <th className="text-left px-4 py-3 font-semibold text-txt-secondary">
                  Source Name
                </th>
                <th className="text-left px-4 py-3 font-semibold text-txt-secondary">
                  Type
                </th>
                <th className="text-left px-4 py-3 font-semibold text-txt-secondary">
                  Agent
                </th>
                <th className="text-right px-4 py-3 font-semibold text-txt-secondary">
                  Contacts
                </th>
                <th className="text-left px-4 py-3 font-semibold text-txt-secondary">
                  Created
                </th>
              </tr>
            </thead>
            <tbody>
              {allSources.map((source) => (
                <tr
                  key={source.id}
                  className="border-b border-border hover:bg-slate-50 transition-colors"
                >
                  <td className="px-4 py-3 font-medium text-txt-primary">{source.name}</td>
                  <td className="px-4 py-3">
                    <SourceBadge type={source.type} size="sm" />
                  </td>
                  <td className="px-4 py-3 text-txt-secondary">{source.agent_name}</td>
                  <td className="px-4 py-3 text-right text-txt-secondary">
                    {source.contact_count}
                  </td>
                  <td className="px-4 py-3 text-txt-secondary">
                    {new Date(source.created_at).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )

  /* ── Render main content based on active view ────────────────────────── */

  const renderContent = () => {
    switch (activeView) {
      case 'master':
      case 'my-contacts':
        return renderContactsDirectory()
      case 'by-source':
        return renderBySourceView()
      case 'merge-queue':
        return renderMergeQueueView()
      case 'import-history':
        return renderImportHistoryView()
      default:
        return renderContactsDirectory()
    }
  }

  /* ── Main render ────────────────────────────────────────────────────── */

  return (
    <div className="h-full flex flex-col bg-surface">
      {/* Sub-navigation tabs */}
      <div className="flex-shrink-0 border-b border-border bg-white">
        <div className="flex overflow-x-auto">
          {[
            { id: 'master', label: 'Master Directory' },
            { id: 'my-contacts', label: 'My Contacts' },
            { id: 'by-source', label: 'By Source' },
            { id: 'merge-queue', label: 'Merge Queue' },
            { id: 'import-history', label: 'Import History' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => {
                setActiveView(tab.id as ActiveView)
                setCurrentPage(1)
              }}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeView === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-txt-secondary hover:text-txt-primary'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content area */}
      <div className="flex-1 min-h-0">{renderContent()}</div>

      {/* Detail Panel */}
      {showDetailPanel && editingContact && (
        <div className="fixed inset-0 z-40 overflow-hidden">
          <div
            className="absolute inset-0 bg-black/20"
            onClick={() => setShowDetailPanel(false)}
          />
          <div className="absolute right-0 top-0 bottom-0 w-96 bg-white shadow-2xl flex flex-col">
            <div className="flex-shrink-0 border-b border-border px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-bold text-txt-primary">Contact Details</h2>
              </div>
              <button
                onClick={() => setShowDetailPanel(false)}
                className="text-txt-tertiary hover:text-txt-primary"
              >
                ✕
              </button>
            </div>

            {/* Detail panel tabs */}
            <div className="flex-shrink-0 border-b border-border flex">
              <button
                onClick={() => setDetailPanelTab('details')}
                className={`flex-1 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  detailPanelTab === 'details'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-txt-secondary'
                }`}
              >
                Details
              </button>
              <button
                onClick={() => setDetailPanelTab('sources')}
                className={`flex-1 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  detailPanelTab === 'sources'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-txt-secondary'
                }`}
              >
                Sources & History
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
              {detailPanelTab === 'details' ? (
                <>
                  {/* Avatar & Name */}
                  <div className="flex items-center gap-4">
                    <ContactAvatar
                      firstName={editingContact.first_name}
                      lastName={editingContact.last_name}
                      size="lg"
                    />
                    <div className="flex-1">
                      <input
                        type="text"
                        value={editingContact.first_name || ''}
                        onChange={(e) =>
                          setEditingContact({
                            ...editingContact,
                            first_name: e.target.value,
                          })
                        }
                        placeholder="First name"
                        className="w-full text-lg font-bold text-txt-primary mb-1 px-2 py-1 rounded border border-border"
                      />
                      <input
                        type="text"
                        value={editingContact.last_name || ''}
                        onChange={(e) =>
                          setEditingContact({
                            ...editingContact,
                            last_name: e.target.value,
                          })
                        }
                        placeholder="Last name"
                        className="w-full text-sm text-txt-secondary px-2 py-1 rounded border border-border"
                      />
                    </div>
                  </div>

                  {/* Contact Info */}
                  <div className="space-y-3">
                    <div>
                      <label className="text-xs font-semibold text-txt-secondary uppercase">
                        Email
                      </label>
                      <input
                        type="email"
                        value={editingContact.email || ''}
                        onChange={(e) =>
                          setEditingContact({
                            ...editingContact,
                            email: e.target.value,
                          })
                        }
                        placeholder="email@example.com"
                        className="w-full mt-1 px-3 py-2 rounded border border-border text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-txt-secondary uppercase">
                        Phone
                      </label>
                      <input
                        type="tel"
                        value={editingContact.phone || ''}
                        onChange={(e) =>
                          setEditingContact({
                            ...editingContact,
                            phone: e.target.value,
                          })
                        }
                        placeholder="+1 (555) 123-4567"
                        className="w-full mt-1 px-3 py-2 rounded border border-border text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-txt-secondary uppercase">
                        Company
                      </label>
                      <input
                        type="text"
                        value={editingContact.company || ''}
                        onChange={(e) =>
                          setEditingContact({
                            ...editingContact,
                            company: e.target.value,
                          })
                        }
                        placeholder="Company name"
                        className="w-full mt-1 px-3 py-2 rounded border border-border text-sm"
                      />
                    </div>
                  </div>

                  {/* Labels */}
                  <div>
                    <label className="text-xs font-semibold text-txt-secondary uppercase">
                      Labels
                    </label>
                    <div className="mt-2 flex flex-wrap gap-1">
                      {editingContact.labels?.map((label) => (
                        <span
                          key={label}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-700"
                        >
                          {label}
                          <button
                            onClick={() =>
                              setEditingContact({
                                ...editingContact,
                                labels: editingContact.labels?.filter(
                                  (l) => l !== label
                                ),
                              })
                            }
                            className="ml-1 text-blue-500 hover:text-blue-900"
                          >
                            ✕
                          </button>
                        </span>
                      ))}
                    </div>
                    <input
                      type="text"
                      value={newLabelInput}
                      onChange={(e) => setNewLabelInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && newLabelInput.trim()) {
                          setEditingContact({
                            ...editingContact,
                            labels: [
                              ...(editingContact.labels || []),
                              newLabelInput.trim(),
                            ],
                          })
                          setNewLabelInput('')
                        }
                      }}
                      placeholder="Type and press Enter"
                      className="w-full mt-2 px-3 py-2 rounded border border-border text-sm"
                    />
                  </div>

                  {/* Notes */}
                  <div>
                    <label className="text-xs font-semibold text-txt-secondary uppercase">
                      Notes
                    </label>
                    <textarea
                      value={editingContact.notes || ''}
                      onChange={(e) =>
                        setEditingContact({
                          ...editingContact,
                          notes: e.target.value,
                        })
                      }
                      placeholder="Add notes..."
                      className="w-full mt-2 px-3 py-2 rounded border border-border text-sm h-24 resize-none"
                    />
                  </div>
                </>
              ) : (
                <>
                  {/* Sources */}
                  {contactSources.length > 0 && (
                    <div>
                      <h3 className="font-semibold text-txt-primary mb-3">
                        Contributing Sources
                      </h3>
                      <div className="space-y-2">
                        {contactSources.map((source) => (
                          <div
                            key={source.id}
                            className="p-3 border border-border rounded-lg bg-slate-50"
                          >
                            <div className="flex items-center justify-between mb-2">
                              <p className="font-medium text-txt-primary">
                                {source.source_name}
                              </p>
                              {source.source_type && (
                                <SourceBadge type={source.source_type} size="sm" />
                              )}
                            </div>
                            <p className="text-xs text-txt-secondary">
                              Added: {new Date(source.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Merge History */}
                  {contactMergeHistory.length > 0 && (
                    <div>
                      <h3 className="font-semibold text-txt-primary mb-3">
                        Merge History
                      </h3>
                      <div className="space-y-2">
                        {contactMergeHistory.map((entry) => (
                          <div
                            key={entry.id}
                            className="p-3 border border-border rounded-lg bg-blue-50"
                          >
                            <p className="text-sm font-medium text-txt-primary">
                              {entry.action === 'merged' ? 'Merged' : 'Updated'}
                            </p>
                            {entry.source_name && (
                              <p className="text-xs text-txt-secondary">
                                Source: {entry.source_name}
                              </p>
                            )}
                            {entry.fields_updated && entry.fields_updated.length > 0 && (
                              <p className="text-xs text-txt-secondary">
                                Fields: {entry.fields_updated.join(', ')}
                              </p>
                            )}
                            <p className="text-xs text-txt-tertiary">
                              {new Date(entry.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {contactSources.length === 0 && contactMergeHistory.length === 0 && (
                    <p className="text-sm text-txt-secondary">
                      No sources or merge history for this contact
                    </p>
                  )}
                </>
              )}
            </div>

            <div className="flex-shrink-0 border-t border-border px-6 py-4 flex items-center justify-between gap-2">
              <button
                onClick={() => handleDeleteContact(editingContact.id || '')}
                className="px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded transition-colors"
              >
                Delete
              </button>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowDetailPanel(false)}
                  className="px-4 py-2 text-sm rounded border border-border hover:bg-slate-50"
                >
                  Cancel
                </button>
                {detailPanelTab === 'details' && (
                  <button
                    onClick={handleSaveContact}
                    className="px-4 py-2 text-sm rounded bg-blue-500 text-white hover:bg-blue-600"
                  >
                    Save
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* New Contact Modal */}
      {showNewContactModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20">
          <div className="bg-white rounded-lg shadow-2xl w-96 p-6 space-y-4">
            <h2 className="text-lg font-bold text-txt-primary">New Contact</h2>

            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-txt-secondary">
                  First Name *
                </label>
                <input
                  type="text"
                  value={formData.first_name || ''}
                  onChange={(e) =>
                    setFormData({ ...formData, first_name: e.target.value })
                  }
                  placeholder="John"
                  className="w-full mt-1 px-3 py-2 rounded border border-border text-sm"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-txt-secondary">
                  Last Name
                </label>
                <input
                  type="text"
                  value={formData.last_name || ''}
                  onChange={(e) =>
                    setFormData({ ...formData, last_name: e.target.value })
                  }
                  placeholder="Doe"
                  className="w-full mt-1 px-3 py-2 rounded border border-border text-sm"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-txt-secondary">
                  Email
                </label>
                <input
                  type="email"
                  value={formData.email || ''}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  placeholder="john@example.com"
                  className="w-full mt-1 px-3 py-2 rounded border border-border text-sm"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-txt-secondary">
                  Phone
                </label>
                <input
                  type="tel"
                  value={formData.phone || ''}
                  onChange={(e) =>
                    setFormData({ ...formData, phone: e.target.value })
                  }
                  placeholder="+1 (555) 123-4567"
                  className="w-full mt-1 px-3 py-2 rounded border border-border text-sm"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-txt-secondary">
                  Company
                </label>
                <input
                  type="text"
                  value={formData.company || ''}
                  onChange={(e) =>
                    setFormData({ ...formData, company: e.target.value })
                  }
                  placeholder="Acme Inc"
                  className="w-full mt-1 px-3 py-2 rounded border border-border text-sm"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-txt-secondary">
                  Labels
                </label>
                <div className="mt-2 flex flex-wrap gap-1">
                  {formData.labels?.map((label) => (
                    <span
                      key={label}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-700"
                    >
                      {label}
                      <button
                        onClick={() =>
                          setFormData({
                            ...formData,
                            labels: formData.labels?.filter((l) => l !== label),
                          })
                        }
                        className="ml-1 text-blue-500 hover:text-blue-900"
                      >
                        ✕
                      </button>
                    </span>
                  ))}
                </div>
                <input
                  type="text"
                  value={newLabelInput}
                  onChange={(e) => setNewLabelInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && newLabelInput.trim()) {
                      setFormData({
                        ...formData,
                        labels: [
                          ...(formData.labels || []),
                          newLabelInput.trim(),
                        ],
                      })
                      setNewLabelInput('')
                    }
                  }}
                  placeholder="Type and press Enter"
                  className="w-full mt-2 px-3 py-2 rounded border border-border text-sm"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-txt-secondary">
                  Notes
                </label>
                <textarea
                  value={formData.notes || ''}
                  onChange={(e) =>
                    setFormData({ ...formData, notes: e.target.value })
                  }
                  placeholder="Add notes..."
                  className="w-full mt-1 px-3 py-2 rounded border border-border text-sm h-20 resize-none"
                />
              </div>
            </div>

            <div className="flex gap-2 justify-end pt-4">
              <button
                onClick={() => setShowNewContactModal(false)}
                className="px-4 py-2 text-sm rounded border border-border hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={handleNewContact}
                className="px-4 py-2 text-sm rounded bg-blue-500 text-white hover:bg-blue-600"
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl p-6 space-y-4 max-h-[90vh] overflow-y-auto">
            {importStep === 'source' ? (
              <>
                <h2 className="text-lg font-bold text-txt-primary">Import Contacts</h2>
                <p className="text-sm text-txt-secondary">Step 1: Select or create a source</p>

                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-txt-secondary">
                      Choose existing source
                    </label>
                    <select
                      value={selectedSource?.id || ''}
                      onChange={(e) => {
                        const source = allSources.find((s) => s.id === e.target.value)
                        setSelectedSource(source || null)
                      }}
                      className="w-full mt-2 px-3 py-2 rounded border border-border text-sm"
                    >
                      <option value="">-- Select a source or create new --</option>
                      {allSources.map((source) => (
                        <option key={source.id} value={source.id}>
                          {source.name} ({source.type})
                        </option>
                      ))}
                    </select>
                  </div>

                  {!selectedSource && (
                    <>
                      <div className="border-t border-border pt-4">
                        <p className="text-sm font-medium text-txt-secondary mb-3">
                          Or create a new source
                        </p>
                        <div className="space-y-3">
                          <div>
                            <label className="text-sm font-medium text-txt-secondary">
                              Source Name
                            </label>
                            <input
                              type="text"
                              value={newSourceName}
                              onChange={(e) => setNewSourceName(e.target.value)}
                              placeholder="e.g. iPhone Contacts, CRM Export"
                              className="w-full mt-1 px-3 py-2 rounded border border-border text-sm"
                            />
                          </div>
                          <div>
                            <label className="text-sm font-medium text-txt-secondary">
                              Source Type
                            </label>
                            <select
                              value={newSourceType}
                              onChange={(e) => setNewSourceType(e.target.value)}
                              className="w-full mt-1 px-3 py-2 rounded border border-border text-sm"
                            >
                              <option value="iphone">iPhone</option>
                              <option value="whatsapp">WhatsApp</option>
                              <option value="android">Android</option>
                              <option value="csv">CSV</option>
                              <option value="manual">Manual</option>
                              <option value="api">API</option>
                            </select>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>

                <div className="flex gap-2 justify-end pt-4">
                  <button
                    onClick={() => setShowImportModal(false)}
                    className="px-4 py-2 text-sm rounded border border-border hover:bg-slate-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => setImportStep('file')}
                    disabled={!selectedSource && !newSourceName}
                    className="px-4 py-2 text-sm rounded bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </>
            ) : importStep === 'file' ? (
              <>
                <h2 className="text-lg font-bold text-txt-primary">Import Contacts</h2>
                <p className="text-sm text-txt-secondary">Step 2: Upload file</p>

                <div
                  onDrop={(e) => {
                    e.preventDefault()
                    const file = e.dataTransfer.files[0]
                    if (file) handleImportFile(file)
                  }}
                  onDragOver={(e) => e.preventDefault()}
                  className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center hover:border-slate-400 transition-colors cursor-pointer"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <svg
                    className="w-12 h-12 text-slate-400 mx-auto mb-3"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M12 4v16m8-8H4"
                    />
                  </svg>
                  <p className="font-medium text-txt-primary">
                    Drop file here or click to browse
                  </p>
                  <p className="text-sm text-txt-secondary mt-1">Supports .vcf, .csv, .xlsx</p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    hidden
                    accept=".vcf,.csv,.xlsx"
                    onChange={(e) => {
                      const file = e.currentTarget.files?.[0]
                      if (file) handleImportFile(file)
                    }}
                  />
                </div>

                <div className="flex gap-2 justify-end pt-4">
                  <button
                    onClick={() => {
                      setImportStep('source')
                      setImportFile(null)
                      setImportPreview(null)
                    }}
                    className="px-4 py-2 text-sm rounded border border-border hover:bg-slate-50"
                  >
                    Back
                  </button>
                  <button
                    onClick={() => setShowImportModal(false)}
                    className="px-4 py-2 text-sm rounded border border-border hover:bg-slate-50"
                  >
                    Cancel
                  </button>
                </div>
              </>
            ) : importStep === 'map' ? (
              <>
                <h2 className="text-lg font-bold text-txt-primary">Import Contacts</h2>
                <p className="text-sm text-txt-secondary">Step 3: Map fields & preview</p>

                <div className="bg-slate-50 p-4 rounded">
                  <p className="text-sm font-medium text-txt-primary">
                    File: {importFile?.name || 'Unknown'}
                  </p>
                  <p className="text-sm text-txt-secondary mt-1">
                    Found {importPreview?.rows.length} contacts
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold text-txt-primary mb-3">Map fields</h3>
                  <div className="space-y-2">
                    {importPreview?.headers.map((header) => (
                      <div key={header} className="flex items-center gap-2">
                        <label className="w-32 text-sm text-txt-secondary">{header}</label>
                        <select
                          value={importFieldMap[header] || 'skip'}
                          onChange={(e) =>
                            setImportFieldMap({
                              ...importFieldMap,
                              [header]: e.target.value,
                            })
                          }
                          className="flex-1 px-3 py-1 rounded border border-border text-sm"
                        >
                          <option value="skip">Skip</option>
                          <option value="first_name">First Name</option>
                          <option value="last_name">Last Name</option>
                          <option value="email">Email</option>
                          <option value="phone">Phone</option>
                          <option value="company">Company</option>
                        </select>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-slate-50 p-4 rounded max-h-48 overflow-auto">
                  <h3 className="font-semibold text-txt-primary mb-2">
                    Preview (first 5 rows)
                  </h3>
                  <table className="text-xs w-full">
                    <thead>
                      <tr className="border-b">
                        {importPreview?.headers.map((h) => (
                          <th key={h} className="text-left px-2 py-1 text-txt-secondary">
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {importPreview?.rows.slice(0, 5).map((row, i) => (
                        <tr key={i} className="border-b">
                          {importPreview.headers.map((h) => (
                            <td key={h} className="px-2 py-1 text-txt-primary">
                              {row[h] || '—'}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="flex gap-2 justify-end pt-4">
                  <button
                    onClick={() => {
                      setImportStep('file')
                      setImportFile(null)
                      setImportPreview(null)
                    }}
                    className="px-4 py-2 text-sm rounded border border-border hover:bg-slate-50"
                  >
                    Back
                  </button>
                  <button
                    onClick={() => setShowImportModal(false)}
                    className="px-4 py-2 text-sm rounded border border-border hover:bg-slate-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleImport}
                    disabled={importProgress.importing}
                    className="px-4 py-2 text-sm rounded bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-50"
                  >
                    Import {importPreview?.rows.length} Contacts
                  </button>
                </div>
              </>
            ) : (
              <>
                <h2 className="text-lg font-bold text-txt-primary">Import Complete</h2>

                <div className="bg-green-50 p-4 rounded border border-green-200">
                  <p className="text-sm font-medium text-green-900">
                    Import finished successfully!
                  </p>
                  <div className="mt-3 space-y-2 text-sm text-green-800">
                    <p>Created: {importProgress.created} new contacts</p>
                    <p>Merged: {importProgress.merged} contacts</p>
                    <p>Queued for review: {importProgress.queued} contacts</p>
                    <p>Skipped: {importProgress.skipped} invalid records</p>
                  </div>
                </div>

                <div className="flex gap-2 justify-end pt-4">
                  <button
                    onClick={() => {
                      setShowImportModal(false)
                      setImportStep('source')
                      setImportFile(null)
                      setImportPreview(null)
                      setSelectedSource(null)
                      setNewSourceName('')
                      setNewSourceType('csv')
                      fetchContacts(activeView === 'my-contacts')
                    }}
                    className="px-4 py-2 text-sm rounded bg-blue-500 text-white hover:bg-blue-600"
                  >
                    Done
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
